import { useEffect, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  AddMeterReadingRequest,
  AvailableMeterRegistersResponse,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadingsRegisters,
  postEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { typedYupResolver } from '@eneco/hooks/form';
import { Bucket, Stack, Text, Button, InputMeterReading, Heading, ButtonLink } from '@eneco/sparky';
import { Input } from '@eneco/sparky/components/Input/Input';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { EnterMeterReadingsRendering, Fields } from '@sitecore/types/EnterMeterReadings';
import { format, isValid } from 'date-fns';
import { useForm, FormProvider, useFormContext, Controller, UseFormSetError, UseFormWatch } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { useQueryParam, StringParam } from 'use-query-params';

import { mapFromTimeframeType, mapToDirectionType, mapToEnergyType, mapToRegisterType } from './MappingHelper';
import { AddMeterReadingFormSchema, getFormSchema } from './ValidationSchema';
import DataErrorNotification from '../../../../components/DataErrorNotification';
import { RoundedSkeleton } from '../../../../components/Loading/RoundedSkeleton';
import ReadFormField from '../../../../components/ReadFormField';
import { useRedirectAndNotifyBE } from '../../../../hooks/useRedirectAndNotifyBE';

type MeterReadingFieldsProps = {
  meterRegisters: AvailableMeterRegistersResponse;
  accountNumber: string;
  fields: Fields;
  isLoading: boolean;
};

const inputMeterReadingFieldsOrder = [
  'ElectricityOfftakeTotalHours',
  'ElectricityOfftakeSingle',
  'ElectricityOfftakeDay',
  'ElectricityOfftakeNight',
  'ElectricityOfftakeExclusiveNight',
  'ElectricityInjectionTotalHours',
  'ElectricityInjectionDay',
  'ElectricityInjectionNight',
  'ElectricityInjectionExclusiveNight',
  'GasOfftakeTotalHours',
];

const handleBackendValidationErrors = async (
  errors: string[],
  setError: UseFormSetError<AddMeterReadingFormSchema>,
  watch: UseFormWatch<AddMeterReadingFormSchema>,
  fields: Fields,
  trackEventBE: ReturnType<typeof useTracking>['trackEventBE'],
) => {
  const meterReadings = watch('meterReadings') || [];

  const setAllReadingErrors = (message: string) => {
    meterReadings.forEach((_, index) => {
      setError(`meterReadings.${index}.readingValue`, { message });
    });
  };

  const setMeterReadingError = (message: string, direction: string, timeFrameType?: string) => {
    meterReadings.forEach((x, index) => {
      if (x.direction === direction && (timeFrameType === undefined || x.meterTimeFrameType === timeFrameType)) {
        setError(`meterReadings.${index}.readingValue`, { message });
      }
    });
  };

  const tooHighMessage =
    fields?.data?.meterReadingTooHighErrorText?.value ??
    'De nieuwe meterstanden zijn te hoog. Kloppen deze? [not in sitecore]';
  const tooLowMessage =
    fields?.data?.meterReadingTooLowErrorText?.value ??
    'De nieuwe meterstanden zijn te laag. Kloppen deze? [not in sitecore]';

  type MeterReadingErrorEntry = { message: string; direction: string; timeFrameType: string };
  const meterReadingErrorMap: Record<string, MeterReadingErrorEntry> = {
    'MeterReading.NewMeterReadingsToHigh.HI.OffTake': {
      message: tooHighMessage,
      direction: 'Offtake',
      timeFrameType: 'Day',
    },
    'MeterReading.NewMeterReadingsToHigh.LO.OffTake': {
      message: tooHighMessage,
      direction: 'Offtake',
      timeFrameType: 'Night',
    },
    'MeterReading.NewMeterReadingsToHigh.HI.Injection': {
      message: tooHighMessage,
      direction: 'Injection',
      timeFrameType: 'Day',
    },
    'MeterReading.NewMeterReadingsToHigh.LO.Injection': {
      message: tooHighMessage,
      direction: 'Injection',
      timeFrameType: 'Night',
    },
    'MeterReading.NewMeterReadingsToHigh.TH.OffTake': {
      message: tooHighMessage,
      direction: 'Offtake',
      timeFrameType: 'Single',
    },
    'MeterReading.NewMeterReadingsToHigh.TH.Injection': {
      message: tooHighMessage,
      direction: 'Offtake',
      timeFrameType: 'Single',
    },
    'MeterReading.NewReadingsShouldBeHigher.HI.OffTake': {
      message: tooLowMessage,
      direction: 'Offtake',
      timeFrameType: 'Day',
    },
    'MeterReading.NewReadingsShouldBeHigher.LO.OffTake': {
      message: tooLowMessage,
      direction: 'Offtake',
      timeFrameType: 'Night',
    },
    'MeterReading.NewReadingsShouldBeHigher.HI.Injection': {
      message: tooLowMessage,
      direction: 'Injection',
      timeFrameType: 'Day',
    },
    'MeterReading.NewReadingsShouldBeHigher.LO.Injection': {
      message: tooLowMessage,
      direction: 'Injection',
      timeFrameType: 'Night',
    },
    'MeterReading.NewReadingsShouldBeHigher.TH.OffTake': {
      message: tooLowMessage,
      direction: 'Offtake',
      timeFrameType: 'Single',
    },
    'MeterReading.NewReadingsShouldBeHigher.TH.Injection': {
      message: tooLowMessage,
      direction: 'Offtake',
      timeFrameType: 'Single',
    },
  };

  for (const error of errors) {
    await trackEventBE('energy_monitor_add', { status: 'failed', message: error });
    if (error === 'MeterReading.AlreadyExists') {
      setError('meterReadingDate', {
        message:
          fields?.data?.readingAlreadyExistsOnDateErrorText?.value ??
          'Er is al een meterstand ingevoerd voor deze datum [not in sitecore]',
      });
    } else if (error === 'AddMeterReadings.Contract.NotFound') {
      setAllReadingErrors(
        fields?.data?.contractNotFoundErrorText?.value ??
          'Er is geen contract gevonden voor deze meter [not in sitecore]',
      );
    } else if (error === 'MeterReading.DateIsEarlierThanContractStart') {
      setError('meterReadingDate', {
        message:
          fields?.data?.dateIsBeforeContractStartErrorText?.value ??
          'Datum mag niet voor startdatum contract liggen [not in sitecore]',
      });
    } else if (error === 'MeterReading.AddingInjectionReadingsToConsumptionOnlyMeter') {
      setMeterReadingError(fields?.data?.meterReadingInjectionNotAllowedText?.value, 'Injection');
    } else {
      const entry = meterReadingErrorMap[error];
      if (entry) {
        setMeterReadingError(entry.message, entry.direction, entry.timeFrameType);
      }
    }
  }
};

const MeterReadingFields = ({ meterRegisters, accountNumber, fields, isLoading }: MeterReadingFieldsProps) => {
  const { register, formState, setValue, getValues, control, watch } = useFormContext<AddMeterReadingFormSchema>();

  useEffect(() => {
    if (!meterRegisters?.registers?.length) return;

    const currentDate = getValues('meterReadingDate');

    const allReadings = meterRegisters.registers.map(register => ({
      accountNumber,
      readingValue: 0,
      ean: meterRegisters.ean,
      meterTimeFrameType: mapToRegisterType(register.meterTimeFrameType),
      utilityType: meterRegisters.energyType,
      direction: register.direction === 'OffTake' ? 'Offtake' : 'Injection',
    }));

    const sortedReadings = [...allReadings].sort((a, b) => {
      const keyA = a.utilityType + a.direction + a.meterTimeFrameType;
      const keyB = b.utilityType + b.direction + b.meterTimeFrameType;

      const indexA = inputMeterReadingFieldsOrder.indexOf(keyA);
      const indexB = inputMeterReadingFieldsOrder.indexOf(keyB);

      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    setValue('meterReadingDate', currentDate, { shouldValidate: false });
    setValue('meterReadings', sortedReadings, { shouldValidate: false });
  }, [meterRegisters, accountNumber, getValues, setValue]);

  const meterReadingsForm = watch('meterReadings');
  const meterReadingDate = watch('meterReadingDate');

  return (
    <Stack>
      <Stack gap="6">
        <Stack gap="2">
          <Input
            type="date"
            label={fields.data.dateFormField.value.label ?? 'Datum [not in sitecore]'}
            value={isValid(meterReadingDate) ? format(meterReadingDate, 'yyyy-MM-dd') : ''}
            {...register('meterReadingDate', { valueAsDate: true })}
            autoComplete="off"
            error={formState.errors.meterReadingDate?.message}
          />
          <Text size="BodyS">
            {fields.data.dateText.value ?? 'Sommige datums kunnen niet worden geselecteerd [not in sitecore]'}
          </Text>
        </Stack>
        <Stack gap="6">
          {isLoading && (
            <Stack gap={8}>
              <Stack gap={2}>
                <RoundedSkeleton width={'50%'} height={24} />
                <RoundedSkeleton width={'100%'} height={48} />
              </Stack>
              <Stack gap={2}>
                <RoundedSkeleton width={'50%'} height={24} />
                <RoundedSkeleton width={'100%'} height={48} />
              </Stack>
            </Stack>
          )}
          {!isLoading &&
            meterReadingsForm?.map((reading, idx: number) => (
              <Controller
                key={idx}
                name={`meterReadings.${idx}.readingValue`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <InputMeterReading
                    key={`${reading.meterTimeFrameType}_${idx}`}
                    unit={meterRegisters.energyType === 'Electricity' ? 'kWh' : 'm3'}
                    label={
                      fields.data.meterReadingsTypesList.value?.enum.find(
                        x => x.name === reading.utilityType + reading.direction + reading.meterTimeFrameType,
                      )?.label || ''
                    }
                    {...field}
                    error={error?.message}
                    maximumFractionDigits={0}
                  />
                )}
              />
            ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

const mapFormToPayload = (data: AddMeterReadingFormSchema) => {
  return (
    data?.meterReadings?.map<AddMeterReadingRequest>(m => ({
      accountNumber: m.accountNumber,
      readingValue: m.readingValue,
      ean: m.ean,
      meterTimeFrameType: mapFromTimeframeType(m.meterTimeFrameType),
      registeredDate: format(data.meterReadingDate!, 'yyyy-MM-dd'),
      utilityType: mapToEnergyType(m.utilityType ?? ''),
      direction: mapToDirectionType(m.direction ?? ''),
    })) ?? []
  );
};

const AddMeterReading = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [ean] = useQueryParam('ean', StringParam);
  const [addressIdentifier] = useQueryParam('address', StringParam);

  const { data: session } = useSession();
  const redirectAndNotify = useRedirectAndNotifyBE();
  const { mutate } = useSWRConfig();
  const { fields } = useContent<EnterMeterReadingsRendering>();
  const { trackEventBE } = useTracking();
  const FormSchema = getFormSchema(fields);

  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();

  const methods = useForm<AddMeterReadingFormSchema>({
    resolver: typedYupResolver<AddMeterReadingFormSchema>(FormSchema),
    defaultValues: {
      meterReadingDate: new Date(),
    },
  });

  const { setError, watch } = methods;

  const { data, isLoading, error } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadingsRegisters,
    {
      path: { accountNumber },
      query: { ean: ean ?? '' },
    },
    ean ? [`/accounts/${accountNumber}/meter-readings/registers?ean=${ean}`] : null,
    session,
  );

  const meterRegisters = unwrapData(data);

  if (!isLoading && !!error && !meterRegisters) {
    return <DataErrorNotification />;
  }

  const onSubmit = async (data: AddMeterReadingFormSchema) => {
    setIsSubmitting(true);
    const { response, error } = await postEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings({
      body: mapFormToPayload(data),
      path: { accountNumber },
    });

    const validationErrors = (error?.errors?.errors as { Validation?: string[] })?.Validation;
    if (validationErrors !== undefined) {
      handleBackendValidationErrors(validationErrors, setError, watch, fields, trackEventBE);
    }

    if (response.status === 200) {
      mutate(`/accounts/${accountNumber}/meter-readings`, true); // table
      mutate(`/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/consumption`, true); // graph
      await trackEventBE('energy_monitor_add', { status: 'success' });

      const consumptionType = meterRegisters?.energyType ?? 'total';

      redirectAndNotify({
        variant: fields?.data?.successNotification?.value?.variant,
        text: <RichText html={fields?.data?.successNotification?.value?.content} />,
        title: fields?.data?.successNotification?.value?.title,
        route: `${fields?.data?.saveButtonLink?.value?.href}?consumptionType=${consumptionType}&address=${addressIdentifier}`,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <Stack gap="6">
      <Heading size="S" as="h2">
        {fields.data.title.value ?? 'Meterstand toevoegen [not in sitecore]'}
      </Heading>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <Bucket title={fields.data.subTitle.value ?? 'Vul onderstaande gegevens aan [not in sitecore]'}>
            <Bucket.Content>
              <Stack gap="6">
                <RichText
                  html={
                    meterRegisters?.energyType === 'Electricity'
                      ? (fields?.data?.description?.value ??
                        'Je kan hier uw meterstanden nauwkeurig bij te houden. Let op: meterstanden mogen niet ouder zijn dan 1 week en moeten hoger zijn dan je vorige meterstand. Na het invoeren wordt jouw aanbevolen voorschotbedrag automatisch herberekend. [not in sitecore]')
                      : (fields?.data?.gasDescription?.value ??
                        'Je kan hier uw meterstanden nauwkeurig bij te houden. Let op: meterstanden mogen niet ouder zijn dan 1 week en moeten hoger zijn dan je vorige meterstand. Na het invoeren wordt jouw aanbevolen voorschotbedrag automatisch herberekend. [not in sitecore]')
                  }
                />
                <ReadFormField
                  label={fields.data.eanCodeLabel.value ?? 'EAN code [not in sitecore]'}
                  value={ean}
                  isLoading={isLoading}
                />
                <ReadFormField
                  label={fields.data.meterTypeLabel.value ?? 'Type meter [not in sitecore]'}
                  value={
                    fields.data.meterTypesList.value?.enum.find(x => x.name === meterRegisters?.meterType)?.label || ''
                  }
                  isLoading={isLoading}
                />
                {meterRegisters && (
                  <MeterReadingFields
                    meterRegisters={meterRegisters}
                    accountNumber={accountNumber}
                    fields={fields}
                    isLoading={isLoading}
                  />
                )}
              </Stack>
            </Bucket.Content>
            <Bucket.Footer>
              <Stack direction="row" gap={2}>
                <Button action="primary" size="regular" tone="onLight" type="submit" isLoading={isSubmitting}>
                  {fields.data.saveButtonLink.value.text ?? 'Opslaan [not in sitecore]'}
                </Button>
                <ButtonLink
                  action="secondary"
                  tone="onLight"
                  href={`${fields?.data?.cancelButtonLink?.value?.href}?consumptionType=${meterRegisters?.energyType ?? 'total'}&address=${addressIdentifier}`}>
                  <Text>{fields.data.cancelButtonLink.value.text}</Text>
                </ButtonLink>
              </Stack>
            </Bucket.Footer>
          </Bucket>
        </form>
      </FormProvider>
    </Stack>
  );
};
export default AddMeterReading;
